//src/pages/gro/DimensionRiskRulesPage.tsx
import { useMemo, useState } from "react";
import { Eye, Pencil, Plus, Power, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { PERMISSIONS } from "@/lib/permissions";
import {
  useQuestionnaireDimensionRiskRuleLookups,
  useQuestionnaireDimensionRiskRules,
  useSetQuestionnaireDimensionRiskRuleStatus,
} from "@/hooks/useQuestionnaireDimensionRiskRules";
import type { QuestionnaireDimensionRiskRule } from "@/types/questionnaireDimensionRiskRules";

const CONDITION_LABELS: Record<string, string> = {
  gt: "Média da dimensão maior que",
  gte: "Média da dimensão maior ou igual a",
  lt: "Média da dimensão menor que",
  lte: "Média da dimensão menor ou igual a",
  eq: "Média da dimensão igual a",
};

const PRIORITY_LABELS: Record<number, string> = {
  1: "Alta",
  2: "Média",
  3: "Baixa",
};

export default function DimensionRiskRulesPage() {
  const navigate = useNavigate();

  const [dimensionId, setDimensionId] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">(
    "all",
  );
  const [search, setSearch] = useState("");

  const lookupsQ = useQuestionnaireDimensionRiskRuleLookups();
  const rulesQ = useQuestionnaireDimensionRiskRules({
    dimension_id: dimensionId || undefined,
    is_active: activeFilter === "all" ? undefined : activeFilter === "active",
  });

  const setStatusMut = useSetQuestionnaireDimensionRiskRuleStatus();

  const dimensions = lookupsQ.data?.dimensions ?? [];
  const rules = rulesQ.data ?? [];

  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;

    return rules.filter((item) => {
      return (
        (item.dimension_name || "").toLowerCase().includes(q) ||
        (item.risk_name || "").toLowerCase().includes(q)
      );
    });
  }, [rules, search]);

  const busy = setStatusMut.isPending;

  async function handleToggleActive(rule: QuestionnaireDimensionRiskRule) {
    try {
      await setStatusMut.mutateAsync({
        ruleId: rule.id,
        is_active: !rule.is_active,
      });
      toast.success(
        rule.is_active
          ? "Regra desativada com sucesso."
          : "Regra ativada com sucesso.",
      );
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status da regra.");
    }
  }

  return (
    <div>
      <PageHeader
        title="Dimensões x Riscos"
        description="Configure como cada dimensão global sugere riscos a partir da média calculada."
      >
        <Button onClick={() => navigate("/gro/dimension-risk-rules/novo")}>
          <Plus className="mr-2 h-4 w-4" />
          Nova regra
        </Button>
      </PageHeader>

      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.3fr_260px_180px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por dimensão ou risco..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={dimensionId}
            onChange={(e) => setDimensionId(e.target.value)}
          >
            <option value="">Todas as dimensões</option>
            {dimensions.map((dimension) => (
              <option key={dimension.id} value={dimension.id}>
                {dimension.name}
              </option>
            ))}
          </select>

          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={activeFilter}
            onChange={(e) =>
              setActiveFilter(e.target.value as "all" | "active" | "inactive")
            }
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativas</option>
            <option value="inactive">Inativas</option>
          </select>
        </div>
      </Card>

      {rulesQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando regras...
        </Card>
      ) : rulesQ.isError ? (
        <Card className="p-6 text-sm text-destructive">
          {(rulesQ.error as any)?.message || "Erro ao carregar regras."}
        </Card>
      ) : filteredRules.length === 0 ? (
        <EmptyState
          title="Nenhuma regra encontrada"
          description="Cadastre regras para transformar médias de dimensões em riscos sugeridos."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dimensão</TableHead>
                <TableHead>Condição</TableHead>
                <TableHead>Risco sugerido</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[160px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filteredRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{rule.dimension_name}</span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {CONDITION_LABELS[rule.operator] || rule.operator}{" "}
                        {rule.threshold_value}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Prioridade {PRIORITY_LABELS[rule.priority] || String(rule.priority)}
                        {rule.auto_generate_inventory_draft
                          ? " • criação automática no inventário"
                          : ""}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col">
                      <span>{rule.risk_name}</span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge variant={rule.is_active ? "default" : "secondary"}>
                      {rule.is_active ? "Ativa" : "Inativa"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <PermissionIconButton
                        label="Visualizar"
                        permission={PERMISSIONS.QUESTIONNAIRES_READ}
                        onClick={() => navigate(`/gro/dimension-risk-rules/${rule.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label="Editar"
                        permission={PERMISSIONS.QUESTIONNAIRES_WRITE}
                        disabled={busy}
                        onClick={() =>
                          navigate(`/gro/dimension-risk-rules/${rule.id}/editar`)
                        }
                      >
                        <Pencil className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        label={rule.is_active ? "Desativar" : "Ativar"}
                        permission={PERMISSIONS.QUESTIONNAIRES_WRITE}
                        disabled={busy}
                        destructive={rule.is_active}
                        onClick={() => handleToggleActive(rule)}
                      >
                        <Power className="h-4 w-4" />
                      </PermissionIconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
